/*
 * SyncService — Cloud-Sync mit Paragraph-Level Deltas
 *
 * Wird von ProjectManager.flush() aufgerufen, wenn ein Cloud-Projekt
 * geaendert wurde. Sammelt Aenderungen und sendet sie nach 5s Ruhe
 * bidirektional zum Server (Push+Pull).
 */

import { measure, mark } from './syncPerf.js'
import { handleSessionLost } from '../auth/AuthService.js'
import { insertPulledIdsByServerAnchor } from './paragraphUtils.js'

// Outline-Redesign Phase 4.8: Wire-Adapter v4↔v3 entfallen — der Server
// spricht nach Migration 008 selbst v4 (drei Paragraph-Typen). Records
// laufen 1:1 in beide Richtungen ueber den Wire.

// Cloud-Sync Debounce
const SYNC_DELAY = 5000   // 5 Sekunden nach letzter Aenderung
const POLL_INTERVAL = 60000  // 60 Sekunden zwischen automatischen Server-Checks
// Karenz, bevor eine Sync-Stoerung als Warn-Zustand gilt — ein einzelner
// fehlgeschlagener Push, den der naechste Debounce-Tick heilt, soll das
// (!)-Border-Glyph nicht aufblitzen lassen (2026-07-02).
const SYNC_TROUBLE_GRACE_MS = 15000
// Folder-Sync ist user-scoped (nicht project-scoped) — eigener Debouncer
// mit derselben 5s-Delay wie der Projekt-Sync.
const FOLDER_SYNC_DELAY = 5000

/**
 * Pruefarithmetik: zwei sortierte String-Arrays sind genau dann gleich, wenn
 * Laenge und Inhalte passen. Brauchen wir, um beim Initial-Sync zu erkennen,
 * dass die spellcheck_ignored-Liste auf einem anderen Geraet veraendert wurde
 * — ohne unnoetig putProject() aufzurufen.
 */
function _arraysEqualSorted(a, b) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false
    }
    return true
}

export class SyncService {
    /**
     * @param {import('./IndexedDBStore.js').IndexedDBStore} store
     * @param {import('../auth/AuthService.js').AuthService} authService
     */
    constructor(store, authService) {
        this._store = store
        this._authService = authService

        // Zeitstempel des ersten fehlgeschlagenen Syncs seit dem letzten
        // Erfolg — Grundlage fuer hasSyncTrouble() / das (!)-Border-Glyph.
        this._syncTroubleSince = null

        // ProjectManager-Referenz (optional, wird via setProjectManager gesetzt).
        // Ermoeglicht beim aktiven Projekt den Zugriff auf die In-Memory
        // Version-Map und spart damit den getParagraphIdVersionMap-Read.
        this._projectManager = null

        // Pending-Aenderungen pro Projekt: Map<projectId, Set<paragraphId>>
        this._pendingSyncIds = new Map()

        // Pending-Loeschungen pro Projekt: Map<projectId, Set<paragraphId>>
        this._pendingDeleteIds = new Map()

        // Pending-Reorder pro Projekt: paragraphOrder hat sich geaendert
        this._pendingReorder = new Set()

        // Pending-Spellcheck-Meta pro Projekt: spellcheckLang oder
        // spellcheckIgnored hat sich seit dem letzten erfolgreichen Sync
        // geaendert und muss im naechsten Body mit hochgeschoben werden.
        this._pendingSpellcheckMeta = new Set()

        // Pending-Folder-Aenderungen (Dashboard Phase 4). User-scoped, nicht
        // project-scoped. Map<folderId, {type: 'upsert'|'delete', folder?, folderId?}>.
        // Letzter Wille pro Folder gewinnt — wenn der User erst umbenennt und
        // dann loescht, ueberschreibt der Delete das Rename.
        this._pendingFolderChanges = new Map()

        // Pending-Project→Folder Verschiebungen.
        // Map<projectId, {folderId: string|null}>. Letzter Wille gewinnt.
        this._pendingProjectFolderMoves = new Map()

        // Eigener Folder-Sync-Debounce-Timer (user-scoped).
        this._folderSyncTimeout = null

        // Folder-Sync laeuft gerade? Promise-Dedup (analog Projekt-Sync).
        this._folderSyncing = null

        // FolderManager-Referenz fuer Pull-Apply (wird via setFolderManager gesetzt).
        this._folderManager = null

        // Debounce-Timer pro Projekt: Map<projectId, timeoutId>
        this._syncTimeouts = new Map()

        // Letzter Sync-Zeitpunkt pro Projekt: Map<projectId, ISO-String>
        this._lastSyncTimes = new Map()

        // Callback: Wird aufgerufen wenn Server-Aenderungen fuer das aktive Projekt ankommen
        // Signatur: onRemoteChanges(projectId, changes[])
        this.onRemoteChanges = null

        // Sync in Arbeit? Map&lt;projectId, Promise&gt; — parallel laufende
        // Aufrufe fuer dasselbe Projekt teilen sich das Promise (Dedup),
        // und flushNow() kann ohne Polling auf Fertigstellung warten.
        this._syncing = new Map()

        // IDs, die WAEHREND eines laufenden Syncs (erneut) enqueued wurden:
        // Map<projectId, Set<paragraphId>>. Wird von _runSyncProject zu
        // Sync-Beginn angelegt und am Ende entfernt. Das Accepted-Cleanup
        // darf fuer diese IDs den Pending-Eintrag nicht entfernen — der
        // Server hat nur den Stand von VOR dem Edit (System-Review B1).
        this._enqueuedDuringSync = new Map()

        // Periodischer Poll-Timer
        this._pollInterval = null

        // Aktives Projekt (fuer Poll und Visibility-Check)
        this._activeProjectId = null

        // Visibility-Change: Sync wenn Tab wieder sichtbar wird
        this._onVisibilityChange = () => {
            if (document.visibilityState === 'visible' && this._activeProjectId) {
                this._pollActiveProject()
            }
        }
    }

    // ─────────────────────────────────────────────
    // Oeffentliche API
    // ─────────────────────────────────────────────

    /**
     * Verknuepft den ProjectManager. Ermoeglicht Zugriff auf die
     * In-Memory Version-Map des aktiven Projekts (spart getVersionMap-Read
     * beim haeufigsten Sync-Fall).
     * @param {import('./ProjectManager.js').ProjectManager} projectManager
     */
    setProjectManager(projectManager) {
        this._projectManager = projectManager
    }

    /**
     * Verknuepft den FolderManager. Erlaubt dem Pull-Pfad, Server-Folder
     * via applyServerFolder/applyServerFolderDelete in den lokalen Store zu
     * schreiben.
     * @param {import('./FolderManager.js').FolderManager} folderManager
     */
    setFolderManager(folderManager) {
        this._folderManager = folderManager
    }

    /**
     * Startet den periodischen Poll und den Visibility-Listener.
     * Wird nach initialSync() aufgerufen.
     * @param {string} activeProjectId - Aktuell geoeffnetes Projekt
     */
    startPolling(activeProjectId) {
        this._activeProjectId = activeProjectId
        this.stopPolling()
        this._pollInterval = setInterval(() => this._pollActiveProject(), POLL_INTERVAL)
        document.addEventListener('visibilitychange', this._onVisibilityChange)
    }

    /**
     * Stoppt den periodischen Poll und den Visibility-Listener.
     */
    stopPolling() {
        if (this._pollInterval) {
            clearInterval(this._pollInterval)
            this._pollInterval = null
        }
        document.removeEventListener('visibilitychange', this._onVisibilityChange)
    }

    /**
     * Setzt das aktive Projekt (bei Projektwechsel).
     * @param {string} projectId
     */
    setActiveProject(projectId) {
        this._activeProjectId = projectId
    }

    /**
     * Flippt ein lokal-als-Cloud-markiertes Projekt auf local-only um.
     * Wird gerufen, wenn der Server das Projekt nicht mehr kennt (404
     * beim Push, oder fehlt in der initialSync-Liste) — typischerweise
     * weil ein anderes Geraet Cloud-Off mit UUID-Swap gemacht hat
     * (Spec §10.1, Variante "Local-Flip"). Pendings werden vor dem
     * Flip verworfen, damit kein erneuter Push die 404 wiederholt.
     *
     * @param {string} projectId
     * @returns {Promise<boolean>} true wenn lokal geflippt wurde
     */
    async _convertToLocalOnly(projectId) {
        const local = await this._store.getProject(projectId)
        if (!local) return false
        if (!local.cloudEnabled) return false   // schon lokal — no-op
        this.discardProject(projectId)
        local.cloudEnabled = false
        local.syncStatus = 'local'
        local.lastSyncTime = undefined
        delete local.lastSyncTime
        await this._store.putProject(local)
        return true
    }

    /**
     * Verwirft alle Per-Projekt-Caches und Pendings fuer eine Projekt-UUID.
     * Wird vom Cloud-Off-Pfad (Spec §10.1) gerufen, nachdem das Projekt
     * lokal eine neue UUID bekommen hat — die alten Eintraege duerfen
     * keinesfalls noch zum Server gepushed werden.
     *
     * @param {string} projectId
     */
    discardProject(projectId) {
        const t = this._syncTimeouts.get(projectId)
        if (t) {
            clearTimeout(t)
            this._syncTimeouts.delete(projectId)
        }
        this._pendingSyncIds.delete(projectId)
        this._pendingDeleteIds.delete(projectId)
        this._pendingReorder.delete(projectId)
        this._pendingSpellcheckMeta.delete(projectId)
        this._pendingProjectFolderMoves.delete(projectId)
        this._lastSyncTimes.delete(projectId)
        if (this._activeProjectId === projectId) this._activeProjectId = null
    }

    /**
     * Paragraph-IDs zum Sync vormerken (von ProjectManager nach flush()).
     * Startet den 5s-Debounce-Timer.
     *
     * @param {string} projectId
     * @param {string[]} paragraphIds
     */
    enqueueParagraphs(projectId, paragraphIds) {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return

        let pending = this._pendingSyncIds.get(projectId)
        if (!pending) {
            pending = new Set()
            this._pendingSyncIds.set(projectId, pending)
        }

        // Dedup gegen pendingDeleteIds: ein Paragraph kann nicht zugleich
        // upserted UND geloescht werden. Letzter Wille gewinnt — hier Upsert.
        // Klassischer Fall: Undo loescht 8 Absaetze (enqueueDeletes), Redo
        // stellt sie wieder her (enqueueParagraphs) — beides innerhalb des
        // 5s-Debounce. Ohne Dedup wuerde der Sync im selben Request erst
        // inserten, dann sofort wieder loeschen → Daten weg auf dem Server,
        // aber paragraphOrder hat die IDs noch → permanenter Drift-Loop.
        const pendingDeletes = this._pendingDeleteIds.get(projectId)
        for (const id of paragraphIds) {
            pending.add(id)
            if (pendingDeletes) pendingDeletes.delete(id)
        }

        // Laufender Sync? IDs als "waehrend des Roundtrips enqueued"
        // vermerken, damit sein Accepted-Cleanup sie nicht verwirft (B1).
        const inflight = this._enqueuedDuringSync.get(projectId)
        if (inflight) {
            for (const id of paragraphIds) inflight.add(id)
        }

        this._scheduleSyncForProject(projectId)
    }

    /**
     * Entfernt alle Pending-Eintraege fuer ein Projekt — Paragraphen,
     * Deletes und Reorder. Wird vom Cloud-Toggle nach erfolgreichem
     * Direct-Upload aufgerufen, damit kein zweiter Sync dieselben
     * Paragraphen nochmal pusht.
     * @param {string} projectId
     */
    clearPending(projectId) {
        this._pendingSyncIds.delete(projectId)
        this._pendingDeleteIds.delete(projectId)
        this._pendingReorder.delete(projectId)
        const timeout = this._syncTimeouts.get(projectId)
        if (timeout) {
            clearTimeout(timeout)
            this._syncTimeouts.delete(projectId)
        }
    }

    /**
     * Snapshot der aktuellen Pending-Eintraege fuer ein Projekt. Dient als
     * Argument fuer removePending() und ermoeglicht selektives Cleanup ohne
     * Race-Verluste bei parallelen Edits.
     *
     * Hintergrund: Der Cloud-Toggle laedt waehrend mehrerer Sekunden alle
     * Paragraphen direkt zum Server hoch. cloudEnabled=true ist dabei schon
     * gesetzt — parallele Edits laufen also durch enqueueParagraphs in die
     * Pending-Queue. Ein blindes clearPending() am Upload-Ende wuerde diese
     * Edits stillschweigend verwerfen (Server hat die alte Version, Client
     * die neue, beide halten sich fuer "synced"). Mit Snapshot+removePending
     * werden nur die Eintraege konsumiert, die VOR dem Upload-Fenster da
     * waren; alles was waehrenddessen reinkam bleibt fuer den naechsten Sync.
     * @param {string} projectId
     * @returns {{syncIds: string[], deleteIds: string[], reorder: boolean}}
     */
    snapshotPending(projectId) {
        return {
            syncIds: [...(this._pendingSyncIds.get(projectId) || [])],
            deleteIds: [...(this._pendingDeleteIds.get(projectId) || [])],
            reorder: this._pendingReorder.has(projectId)
        }
    }

    /**
     * Selektives Pending-Cleanup. Entfernt genau die im Snapshot enthaltenen
     * IDs, laesst Eintraege die nach dem Snapshot enqueued wurden in Ruhe.
     * @param {string} projectId
     * @param {{syncIds?: string[], deleteIds?: string[], reorder?: boolean}} snapshot
     */
    removePending(projectId, snapshot) {
        const { syncIds = [], deleteIds = [], reorder = false } = snapshot || {}
        const sync = this._pendingSyncIds.get(projectId)
        if (sync) {
            for (const id of syncIds) sync.delete(id)
            if (sync.size === 0) this._pendingSyncIds.delete(projectId)
        }
        const dels = this._pendingDeleteIds.get(projectId)
        if (dels) {
            for (const id of deleteIds) dels.delete(id)
            if (dels.size === 0) this._pendingDeleteIds.delete(projectId)
        }
        if (reorder) this._pendingReorder.delete(projectId)
    }

    /**
     * Geloeschte Paragraph-IDs zum Sync vormerken.
     *
     * @param {string} projectId
     * @param {string[]} paragraphIds
     */
    enqueueDeletes(projectId, paragraphIds) {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return

        let pending = this._pendingDeleteIds.get(projectId)
        if (!pending) {
            pending = new Set()
            this._pendingDeleteIds.set(projectId, pending)
        }

        // Dedup gegen pendingSyncIds (siehe enqueueParagraphs).
        const pendingSyncs = this._pendingSyncIds.get(projectId)
        for (const id of paragraphIds) {
            pending.add(id)
            if (pendingSyncs) pendingSyncs.delete(id)
        }

        // Laufender Sync? IDs vermerken (B1, siehe enqueueParagraphs).
        const inflight = this._enqueuedDuringSync.get(projectId)
        if (inflight) {
            for (const id of paragraphIds) inflight.add(id)
        }

        this._scheduleSyncForProject(projectId)
    }

    /**
     * Meldet eine Reorder-Aenderung an (paragraphOrder hat sich geaendert).
     * @param {string} projectId
     */
    enqueueReorder(projectId) {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return
        this._pendingReorder.add(projectId)
        if (!this._pendingSyncIds.has(projectId)) {
            this._pendingSyncIds.set(projectId, new Set())
        }
        this._scheduleSyncForProject(projectId)
    }

    /**
     * Markiert die Pro-Projekt-Spellcheck-Metadaten (Sprache + Ignore-
     * Liste) als "muss im naechsten Sync mit hochgeschoben werden". Wird
     * vom ProjectManager aufgerufen, sobald `setProjectSpellcheckLang`
     * oder `setProjectSpellcheckIgnore` greift. Idempotent — mehrfaches
     * Setzen vor dem Sync sammelt sich zu einem einzigen Update.
     * @param {string} projectId
     */
    markSpellcheckMetaDirty(projectId) {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return
        this._pendingSpellcheckMeta.add(projectId)
        if (!this._pendingSyncIds.has(projectId)) {
            this._pendingSyncIds.set(projectId, new Set())
        }
        this._scheduleSyncForProject(projectId)
    }

    /**
     * Vormerken einer Folder-Aenderung (Dashboard Phase 4). Letzter Wille
     * pro Folder gewinnt: ein delete ueberschreibt ein vorheriges upsert,
     * ein upsert ueberschreibt ein vorheriges upsert. Folder-Sync ist
     * user-scoped — separater Debounce-Timer.
     *
     * @param {{type: 'upsert', folder: Object} | {type: 'delete', folderId: string}} change
     */
    enqueueFolderChange(change) {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return
        const id = change.type === 'upsert' ? change.folder.id : change.folderId
        if (!id) return
        this._pendingFolderChanges.set(id, change)
        this._scheduleFolderSync()
    }

    /**
     * Vormerken einer Project→Folder-Verschiebung. Eine pendin-Move pro
     * Projekt — letzter Wille gewinnt.
     *
     * @param {string} projectId
     * @param {string|null} folderId
     */
    enqueueProjectFolderMove(projectId, folderId) {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return
        this._pendingProjectFolderMoves.set(projectId, { folderId: folderId || null })
        this._scheduleFolderSync()
    }

    /**
     * Snapshot der aktuellen Folder-Pendings (analog snapshotPending fuer
     * Projekte). Wird intern von `_doFolderSync` genutzt, damit Edits, die
     * waehrend des HTTP-Roundtrips reinkamen, nach Erfolg nicht stillschweigend
     * konsumiert werden. Extern (Cloud-Toggle) heute nicht aufgerufen, weil
     * der Cloud-Toggle project-scoped ist; bleibt fuer kuenftige Re-Auth-Pfade
     * symmetrisch zu `snapshotPending(projectId)` erhalten.
     * @returns {{folderChanges: Array, projectFolderMoves: Array}}
     */
    snapshotFolderPending() {
        return {
            folderChanges: [...this._pendingFolderChanges.values()],
            projectFolderMoves: [...this._pendingProjectFolderMoves.entries()]
                .map(([projectId, val]) => ({ projectId, folderId: val.folderId }))
        }
    }

    /**
     * Erzwingt einen sofortigen Folder-Sync. Bricht den Debounce-Timer ab,
     * wartet auf einen evtl. laufenden Folder-Sync und startet einen frischen.
     * @returns {Promise<void>}
     */
    async flushFoldersNow() {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return
        if (this._folderSyncTimeout) {
            clearTimeout(this._folderSyncTimeout)
            this._folderSyncTimeout = null
        }
        if (this._folderSyncing) {
            try { await this._folderSyncing } catch { /* egal */ }
        }
        await this._runFolderSync()
    }

    /**
     * Initial-Sync: Alle Cloud-Projekte vom Server laden.
     * Wird beim Login / App-Start aufgerufen.
     */
    async initialSync() {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return

        // Laufende Debounce-Timer abbrechen — initialSync uebernimmt den Sync
        for (const [, timeout] of this._syncTimeouts) {
            clearTimeout(timeout)
        }
        this._syncTimeouts.clear()

        try {
            const res = await fetch('/api/sync/projects')
            if (!res.ok) {
                if (res.status === 401) handleSessionLost()
                return
            }

            const { projects, folders, serverTime } = await res.json()

            // Server-Folder spiegeln (Dashboard Phase 4). Der Server ist
            // Source of Truth — lokale Folder ohne Server-Match werden lokal
            // entfernt (sonst bleiben "Folder-Zombies" stehen, die ein anderes
            // Geraet inzwischen geloescht hat). `getAllFolders` filtert per
            // userId, anonyme/Fremd-User-Folder sind hier per Definition
            // unsichtbar und damit nicht gefaehrdet.
            await this._applyServerFolderList(folders)

            // Server-Projekte verarbeiten
            const serverProjectIds = new Set()
            for (const serverProject of projects) {
                serverProjectIds.add(serverProject.id)
                const localProject = await this._store.getProject(serverProject.id)

                // Auf Server geloeschtes Projekt → lokal auch markieren
                if (serverProject.deletedAt) {
                    if (localProject && localProject.cloudEnabled) {
                        localProject.deletedAt = serverProject.deletedAt
                        await this._store.putProject(localProject)
                    }
                    continue
                }

                if (!localProject) {
                    // Neues Projekt vom Server → lokal anlegen
                    await this._store.putProject({
                        id: serverProject.id,
                        title: serverProject.title,
                        author: serverProject.author,
                        userId: this._authService.user?.id,
                        paragraphOrder: serverProject.paragraphOrder || [],
                        cursorState: serverProject.cursorState,
                        cloudEnabled: true,
                        version: serverProject.version,
                        syncStatus: 'synced',
                        createdAt: serverProject.createdAt,
                        updatedAt: serverProject.updatedAt,
                        deletedAt: null,
                        folderId: serverProject.folderId || null,
                        sizeBytes: typeof serverProject.sizeBytes === 'number' ? serverProject.sizeBytes : 0,
                        spellcheckLang: serverProject.spellcheckLang || null,
                        spellcheckIgnored: Array.isArray(serverProject.spellcheckIgnored)
                            ? serverProject.spellcheckIgnored
                            : []
                    })

                    // Paragraphen laden (Initial-Sync mit lastSyncTime=0)
                    await this._pullAllParagraphs(serverProject.id)

                    // lastSyncTime sofort persistieren (ueberlebt Reload ohne beforeunload)
                    const createdProject = await this._store.getProject(serverProject.id)
                    if (createdProject) {
                        createdProject.lastSyncTime = serverTime
                        await this._store.putProject(createdProject)
                    }
                } else if (localProject.cloudEnabled) {
                    // Metadaten vom Server uebernehmen (Rename auf anderem Geraet)
                    let metaChanged = false
                    if (serverProject.title !== localProject.title) {
                        localProject.title = serverProject.title
                        metaChanged = true
                    }
                    if (serverProject.author !== localProject.author) {
                        localProject.author = serverProject.author
                        metaChanged = true
                    }
                    // folderId vom Server uebernehmen, falls keine lokale
                    // Pending-Move auf dieses Projekt wartet (Last-Write-Wins
                    // client-seitig). sizeBytes nehmen wir vom Server nur,
                    // wenn lokal noch nichts berechnet wurde — sonst gewinnt
                    // der inkrementelle Client-Tracker.
                    if (!this._pendingProjectFolderMoves.has(serverProject.id)) {
                        const serverFolderId = serverProject.folderId || null
                        if ((localProject.folderId || null) !== serverFolderId) {
                            localProject.folderId = serverFolderId
                            metaChanged = true
                        }
                    }
                    // sizeBytes vom Server uebernehmen, wann immer er sich
                    // unterscheidet. Vorher nur als Backfill (`local===0`-
                    // Guard), das verpasste den normalen Update-Fall: anderes
                    // Geraet clearet/importiert das Projekt → Server-sizeBytes
                    // aktualisiert sich, hier blieb aber der lokale alte Wert
                    // stehen. Auch sizeBytes=0 muss ankommen (geclearte Projekte).
                    // Der ProjectManager-Tracker ist inkrementell und schreibt
                    // sich bei lokalen pending-Aenderungen via flush() ohnehin
                    // wieder auf den korrekten Wert.
                    if (typeof serverProject.sizeBytes === 'number'
                            && serverProject.sizeBytes !== localProject.sizeBytes) {
                        localProject.sizeBytes = serverProject.sizeBytes
                        metaChanged = true
                    }
                    // Pro-Projekt-Spellcheck (Sprache + Ignore) vom Server
                    // uebernehmen — aber nur, wenn lokal kein pending Update
                    // wartet (Last-Write-Wins client-seitig, wenn der User
                    // gerade umgeschaltet hat).
                    if (!this._pendingSpellcheckMeta.has(serverProject.id)) {
                        const serverLang = serverProject.spellcheckLang || null
                        if ((localProject.spellcheckLang || null) !== serverLang) {
                            localProject.spellcheckLang = serverLang
                            metaChanged = true
                        }
                        const serverIgn = Array.isArray(serverProject.spellcheckIgnored)
                            ? serverProject.spellcheckIgnored
                            : []
                        const localIgn = Array.isArray(localProject.spellcheckIgnored)
                            ? localProject.spellcheckIgnored
                            : []
                        if (!_arraysEqualSorted(localIgn, serverIgn)) {
                            localProject.spellcheckIgnored = serverIgn
                            metaChanged = true
                        }
                    }
                    if (metaChanged) {
                        await this._store.putProject(localProject)
                    }

                    // Paragraphen synchronisieren
                    await this.syncProject(serverProject.id)
                }

                // syncProject setzt _lastSyncTimes bereits korrekt aus seiner
                // eigenen Server-Antwort. Nur setzen wenn noch kein Wert da ist
                // (neues Projekt ohne syncProject-Aufruf).
                if (!this._lastSyncTimes.has(serverProject.id)) {
                    this._lastSyncTimes.set(serverProject.id, serverTime)
                }
            }

            // Cleanup: lokale Cloud-Projekte, die der Server NICHT mehr
            // hat, auf local-only umflippen (Spec §10.1, Local-Flip).
            // Typisch: ein anderes Geraet hat Cloud-Off mit UUID-Swap
            // gemacht und das Server-Projekt hart geloescht — ohne diesen
            // Cleanup haetten wir lokal ein "Cloud-Zombie", das bei jedem
            // Sync-Tick 404 zurueckbekommt.
            const userId = this._authService.user?.id
            if (userId) {
                const localProjects = await this._store.getAllProjects(userId)
                for (const local of localProjects) {
                    if (local.cloudEnabled && !serverProjectIds.has(local.id)) {
                        await this._convertToLocalOnly(local.id)
                    }
                }
            }
        } catch (err) {
            console.warn('Initial-Sync fehlgeschlagen:', err.message)
        }
    }

    /**
     * Bidirektionaler Sync fuer ein einzelnes Projekt.
     * Sendet lokale Aenderungen + empfaengt Server-Aenderungen.
     *
     * Dedup: Laufende Syncs werden per Promise geteilt — parallele Aufrufe
     * erhalten dasselbe Promise zurueck und warten dieselbe Fertigstellung ab.
     *
     * @param {string} projectId
     * @returns {Promise<void>}
     */
    syncProject(projectId) {
        const inflight = this._syncing.get(projectId)
        if (inflight) return inflight

        const promise = this._runSyncProject(projectId).finally(() => {
            // Nur loeschen wenn kein Nachfolge-Promise den Slot belegt hat
            // (race safety — sollte in der Praxis nicht vorkommen, weil
            // inflight-Check oben dedupliziert, aber sicher ist sicher).
            if (this._syncing.get(projectId) === promise) {
                this._syncing.delete(projectId)
            }
        })
        this._syncing.set(projectId, promise)
        return promise
    }

    /**
     * Merkt den Beginn einer Sync-Stoerung (erster Fehlschlag seit dem
     * letzten Erfolg). Erfolgreiche Roundtrips setzen zurueck.
     * @private
     */
    _noteSyncTrouble() {
        if (this._syncTroubleSince === null) this._syncTroubleSince = Date.now()
    }

    /**
     * Ob der Cloud-Sync aktuell gestoert ist: offline, oder Pushes schlagen
     * seit mehr als SYNC_TROUBLE_GRACE_MS fehl. Grundlage fuer das
     * "(!)"-Warn-Glyph am Main-Pane (Testphasen-Entscheidung 2026-07-02).
     * @returns {boolean}
     */
    hasSyncTrouble() {
        if (this._authService.isOffline) return true
        return this._syncTroubleSince !== null
            && (Date.now() - this._syncTroubleSince) > SYNC_TROUBLE_GRACE_MS
    }

    /**
     * Eigentlicher Sync-Body (ohne Dedup-Logik — der Wrapper syncProject
     * kuemmert sich darum).
     * @param {string} projectId
     * @private
     */
    async _runSyncProject(projectId) {
        const endTotal = mark('SyncService.syncProject.total')

        // B1 (System-Review 2026-06-10): Reorder-/Spellcheck-Flags werden zu
        // Sync-Beginn konsumiert; die Fehlerpfade legen sie zurueck.
        // Deklaration ausserhalb des try, damit der catch sie sieht.
        let hasReorder = false
        let hasSpellcheckMeta = false

        try {
            const project = await this._store.getProject(projectId)
            if (!project || !project.cloudEnabled) return

            // B1: Pending-Sets zu Sync-Beginn in den Maps REGISTRIEREN und
            // nur einen Snapshot konsumieren. enqueue* waehrend des HTTP-
            // Roundtrips schreibt dann in die registrierten Live-Sets; das
            // Cleanup am Ende entfernt nur, was dieser Sync tatsaechlich
            // gesendet hat — frische Eintraege ueberleben. (Vorher erzeugte
            // ein Pull-only-Sync ein UNregistriertes lokales Set, und das
            // pauschale Map-Delete am Ende verwarf alles, was waehrend des
            // Roundtrips enqueued wurde — Datenverlust nach Reload moeglich.)
            let livePendingIds = this._pendingSyncIds.get(projectId)
            if (!livePendingIds) {
                livePendingIds = new Set()
                this._pendingSyncIds.set(projectId, livePendingIds)
            }
            let livePendingDeletes = this._pendingDeleteIds.get(projectId)
            if (!livePendingDeletes) {
                livePendingDeletes = new Set()
                this._pendingDeleteIds.set(projectId, livePendingDeletes)
            }
            const pendingIds = new Set(livePendingIds)
            const pendingDeletes = new Set(livePendingDeletes)

            // IDs, die waehrend des Roundtrips (erneut) enqueued werden,
            // sammeln enqueueParagraphs/enqueueDeletes hier ein — das
            // Accepted-Cleanup unten laesst deren Live-Eintraege stehen.
            const enqueuedDuringSync = new Set()
            this._enqueuedDuringSync.set(projectId, enqueuedDuringSync)

            // Flags konsumieren: werden sie waehrend des Roundtrips erneut
            // gesetzt (neuere Order / neuere Spellcheck-Meta), ueberlebt
            // das fuer den naechsten Sync.
            hasReorder = this._pendingReorder.delete(projectId)
            hasSpellcheckMeta = this._pendingSpellcheckMeta.delete(projectId)

            let changes = []

            // Outline-Redesign Phase 4.8: Wire-Adapter weg — Server spricht
            // selbst v4 (Migration 008). Records 1:1 vom IDB-Store auf den Wire.
            const wireParagraphOrder = project.paragraphOrder || []

            if (pendingIds.size > 0 || pendingDeletes.size > 0) {
                const allRecords = await measure(
                    'SyncService.push.getAllRecords',
                    () => this._store.getParagraphsByProject(projectId),
                    r => r.length
                )

                // Stale Pendings aus der Queue entfernen — ID existiert nicht
                // mehr im Store (geloescht oder verschoben). Sonst kommen sie
                // beim naechsten Reload wieder ueber restorePendingState und
                // blockieren den Sync erneut. Aus Snapshot UND Live-Set
                // entfernen — der Snapshot enthaelt nur IDs von vor dem
                // Sync-Start, der Store-Read oben kann sie nicht verpassen.
                const recordIds = new Set(allRecords.map(r => r.id))
                for (const id of [...pendingIds]) {
                    if (!recordIds.has(id)) {
                        pendingIds.delete(id)
                        livePendingIds.delete(id)
                    }
                }

                // Cross-Project-Schutz: nur Records DIESES Projekts
                const recordById = new Map(
                    allRecords
                        .filter(r => r.projectId === projectId)
                        .map(r => [r.id, r])
                )

                for (const id of pendingIds) {
                    const rec = recordById.get(id)
                    if (!rec) continue
                    changes.push({
                        type: 'upsert',
                        id: rec.id,
                        data: rec.data,
                        version: rec.version
                    })
                }
                for (const id of pendingDeletes) {
                    changes.push({ type: 'delete', id })
                }
            }

            // Fallback-Kette: RAM-Cache → IndexedDB-Projekt → Epoch
            const lastSyncTime = this._lastSyncTimes.get(projectId) || project.lastSyncTime || '0'

            // paragraphOrder nur bei Strukturaenderung senden (V4, System-
            // Review 2026-06-10): reines Tippen aendert die Order nicht —
            // bei 5000 Absaetzen waeren das ~190 KB UUID-JSON alle 5s fuer
            // eine Server-No-op. Inserts/Deletes/Moves setzen das Flag ueber
            // ProjectManager.flush → enqueueReorder; der Server laesst bei
            // paragraphOrder:null die Order-Spalte unangetastet (kein Drift-
            // Check — Drift-Detection laeuft damit nur noch bei Struktur-
            // Syncs). hasReorder/hasSpellcheckMeta sind oben zu Sync-Beginn
            // konsumiert worden (B1) — Spellcheck-Meta geht nur im Last-
            // Batch mit, der Server fasst Zwischen-Batches eh nicht an.
            const hasLocalChanges = changes.length > 0

            // Changes byte- und count-basiert chunken — robust gegen grosse
            // Pending-Mengen (z.B. nach Undo mit _allContentDirty, Paste,
            // Import). Zwischen-Batches senden lastSyncTime=jetzt damit der
            // Server keinen Pull macht; nur der letzte Batch bekommt den
            // echten lastSyncTime + cursorState + paragraphOrder und
            // liefert damit das finale Merge/Cleanup-Ergebnis.
            const MAX_BATCH_ITEMS = 500
            const MAX_BATCH_BYTES = 1_500_000
            const batches = []
            if (changes.length === 0) {
                batches.push([])  // Pull-only Sync (kein Push, aber Pull laeuft)
            } else {
                const sizes = changes.map(c => JSON.stringify(c).length)
                let offset = 0
                while (offset < changes.length) {
                    let end = offset
                    let bytes = 0
                    while (
                        end < changes.length &&
                        end - offset < MAX_BATCH_ITEMS &&
                        (bytes === 0 || bytes + sizes[end] <= MAX_BATCH_BYTES)
                    ) {
                        bytes += sizes[end]
                        end++
                    }
                    batches.push(changes.slice(offset, end))
                    offset = end
                }
            }

            let result = null
            for (let i = 0; i < batches.length; i++) {
                const isLastBatch = i === batches.length - 1
                const batch = batches[i]

                const body = {
                    projectId,
                    projectVersion: project.version || 1,
                    // Zwischen-Batches: "jetzt" als lastSyncTime → Server-Pull bleibt leer.
                    // Letzter Batch: echter lastSyncTime → voller Pull.
                    lastSyncTime: isLastBatch ? lastSyncTime : new Date().toISOString(),
                    cursorState: isLastBatch && hasLocalChanges ? project.cursorState : undefined,
                    changes: batch,
                    // Phase 3a.2: Wire-paragraphOrder (Outline-Item-IDs in Pair-
                    // Position rausgenommen, Solo-Items behalten Identity-UUID).
                    paragraphOrder: isLastBatch && hasReorder
                        ? (wireParagraphOrder || null)
                        : null
                }
                if (isLastBatch && hasSpellcheckMeta) {
                    // null fuer spellcheckLang ist semantisch wichtig: heisst
                    // "User-Default verwenden" und ueberschreibt einen evtl.
                    // gesetzten Server-Wert. Schema laesst nullable explizit zu.
                    body.spellcheckLang     = project.spellcheckLang || null
                    body.spellcheckIgnored  = Array.isArray(project.spellcheckIgnored)
                        ? project.spellcheckIgnored
                        : []
                }
                // sizeBytes (Dashboard Phase 4): nur im letzten Batch, nur wenn
                // wir einen Wert haben. Server-Update macht KEIN updated_at-Bump
                // (siehe sync.js), darum kostet das die Last-Modified-Anzeige
                // nichts.
                if (isLastBatch && typeof project.sizeBytes === 'number') {
                    body.sizeBytes = project.sizeBytes
                }

                const res = await fetch('/api/sync', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                })

                if (!res.ok) {
                    if (res.status === 404) {
                        // Server kennt das Projekt nicht mehr — typisch
                        // wenn ein anderes Geraet Cloud-Off mit UUID-Swap
                        // gemacht hat (Spec §10.1, Variante Local-Flip).
                        // Lokal auf cloudEnabled=false flippen, Pendings
                        // (inkl. der konsumierten Flags) verwerfen — der
                        // naechste Tick versucht nicht wieder einen Push.
                        await this._convertToLocalOnly(projectId)
                        return
                    }
                    if (res.status === 401) handleSessionLost()
                    else {
                        console.warn(`Sync fehlgeschlagen fuer Projekt ${projectId} (Batch ${i + 1}/${batches.length}): ${res.status}`)
                        this._noteSyncTrouble()
                    }
                    // B1: konsumierte, aber nicht gesendete Flags zuruecklegen.
                    if (hasReorder) this._pendingReorder.add(projectId)
                    if (hasSpellcheckMeta) this._pendingSpellcheckMeta.add(projectId)
                    return
                }

                // B4 (System-Review 2026-06-10): erfolgreicher Roundtrip
                // beweist Server-Erreichbarkeit — haengendes Offline-Flag
                // (z.B. Boot ohne Netz) zuruecksetzen, damit die enqueue*/
                // flushNow-Gates wieder oeffnen. Sync-Stoerung ist damit
                // ebenfalls beendet.
                this._authService.isOffline = false
                this._syncTroubleSince = null

                const batchResult = await res.json()

                // Accepted aus pending entfernen — pro Batch, damit ein
                // spaeterer Fehler nicht die bereits erfolgreich gepushten
                // IDs wieder verwirft. Phase 4.8: Server bestaetigt v4-UUIDs
                // direkt (kein Wire-Adapter mehr — die zusaetzliche Sidecar-
                // Partner-Entfernung ist entfallen).
                for (const acceptedId of batchResult.accepted) {
                    pendingIds.delete(acceptedId)
                    pendingDeletes.delete(acceptedId)
                    // Live-Eintrag nur entfernen, wenn der Absatz nicht
                    // WAEHREND des Roundtrips erneut enqueued wurde — der
                    // Server hat sonst nur den Stand von vor dem Edit, und
                    // der frischere Push muss erhalten bleiben (B1).
                    if (!enqueuedDuringSync.has(acceptedId)) {
                        livePendingIds.delete(acceptedId)
                        livePendingDeletes.delete(acceptedId)
                    }
                }

                // Soft-Drift-Hinweis: die mitgesendete paragraphOrder passte
                // nicht zur Server-Paragraph-Tabelle. Server hat den Push
                // trotzdem committet, die Order aber nicht uebernommen.
                //
                // Self-Heal: `extra`-IDs sind Waisen — sie stehen lokal in
                // paragraphOrder, fehlen aber auf dem Server. Wir re-enqueuen
                // sie als Upserts, damit der naechste Sync sie hochschiebt.
                // Sind sie lokal in IndexedDB vorhanden (klassischer Undo-Redo-
                // Race-Bug), werden sie inserted und der Drift heilt. Sind sie
                // lokal NICHT vorhanden, filtert die stale-pending-Logik in
                // _runSyncProject sie kommentarlos raus, und der naechste Pull-
                // Pfad ersetzt project.paragraphOrder durch serverParagraphOrder.
                if (batchResult.paragraphOrderDrift) {
                    console.warn('[SyncService] paragraph_order_drift (soft)', {
                        projectId,
                        ...batchResult.paragraphOrderDrift
                    })
                    // Kein _pendingReorder.delete mehr noetig — das Flag ist
                    // zu Sync-Beginn konsumiert (B1). Wurde es waehrend des
                    // Roundtrips erneut gesetzt, ist das eine NEUERE Order,
                    // die der naechste Sync pushen soll.

                    const orphans = batchResult.paragraphOrderDrift.extra
                    if (orphans && orphans.length > 0) {
                        // Orphans in Live-Set UND Snapshot: Live, damit der
                        // naechste Sync sie pusht; Snapshot, damit der
                        // serverParagraphOrder-Cleanup unten sie nicht aus
                        // IndexedDB loescht.
                        for (const id of orphans) {
                            livePendingIds.add(id)
                            pendingIds.add(id)
                            livePendingDeletes.delete(id)
                            pendingDeletes.delete(id)
                        }
                        // V4: Heal-Sync muss die Order MITSCHICKEN — sonst
                        // laeuft die Server-Drift-Detection nicht, und die
                        // Response-serverParagraphOrder (ohne Orphans) wuerde
                        // die frisch accepteten Orphans im Cleanup unten
                        // lokal loeschen. Vor V4 kam die Order implizit ueber
                        // hasLocalChanges mit.
                        this._pendingReorder.add(projectId)
                        // Nicht sofort syncen — der laufende Sync zieht den
                        // Pull-Pfad zu Ende. _scheduleSyncForProject startet
                        // den 5s-Debounce.
                        this._scheduleSyncForProject(projectId)
                    }
                }

                if (isLastBatch) result = batchResult
            }

            // Kompakte lokale Versions-Map — wird von MERGE (Vergleich)
            // und CLEANUP (id-Set) gemeinsam genutzt. Fuer das aktive Projekt
            // liefert der ProjectManager die Map aus seinem In-Memory-Cache
            // (0 DB-Cost); inaktive Projekte fallen auf den Store-Read zurueck.
            const appliedChanges = []
            const localVersions = await measure(
                'SyncService.merge.getVersionMap',
                () => this._projectManager
                    ? this._projectManager.getVersionMap(projectId)
                    : this._store.getParagraphIdVersionMap(projectId),
                r => r.size
            )

            // Konflikte loggen (Last Write Wins — Server-Version uebernehmen).
            // Conflicts werden auch zu appliedChanges gezaehlt, damit
            // onRemoteChanges zuverlaessig feuert und der ProjectManager
            // seine _versionMap invalidiert.
            //
            // Phase 4.8: serverData ist v4 (Server spricht nach Migration 008
            // selbst v4) — direkt in den Store schreiben.
            if ((result.conflicts || []).length > 0) {
                for (const conflict of result.conflicts) {
                    await this._store.putParagraph({
                        id: conflict.id,
                        projectId,
                        data: conflict.serverData,
                        version: conflict.serverVersion,
                        updatedAt: new Date().toISOString()
                    })
                    appliedChanges.push({
                        id: conflict.id, data: conflict.serverData, version: conflict.serverVersion
                    })
                    localVersions.set(conflict.id, conflict.serverVersion)
                    console.warn(`Sync-Konflikt: Paragraph ${conflict.id}`,
                        `Server v${conflict.serverVersion}`)
                    pendingIds.delete(conflict.id)
                    // Live-Eintrag analog zum Accepted-Cleanup schonen, wenn
                    // waehrend des Roundtrips erneut editiert wurde (B1).
                    if (!enqueuedDuringSync.has(conflict.id)) {
                        livePendingIds.delete(conflict.id)
                    }
                }
            }

            // Server-Aenderungen (von anderen Geraeten) uebernehmen
            // Nur wenn die Server-Version neuer ist als die lokale —
            // sonst wuerden ungesyncte lokale Aenderungen ueberschrieben.
            //
            // Phase 4.8: Server liefert v4-Records direkt — kein Adapter mehr.
            const remoteChanges = result.serverChanges || []
            for (const change of remoteChanges) {
                if (change.type === 'delete') {
                    if (localVersions.has(change.id)) {
                        await this._store.deleteParagraph(change.id)
                        localVersions.delete(change.id)
                        appliedChanges.push({ id: change.id, deleted: true })
                    }
                    continue
                }
                const localVersion = localVersions.get(change.id) || 0
                if (!localVersions.has(change.id) || (change.version || 0) > localVersion) {
                    // Echte Aenderung: neuere Version oder neuer Absatz.
                    // Gleiche Version wird uebersprungen — gleiche Version
                    // bedeutet gleichen Inhalt (by design). JSON.stringify-
                    // Vergleich ist unzuverlaessig weil PostgreSQL JSONB
                    // die Key-Reihenfolge aendert.
                    await this._store.putParagraph({
                        id: change.id,
                        projectId,
                        data: change.data,
                        version: change.version,
                        updatedAt: change.updatedAt
                    })
                    appliedChanges.push({ id: change.id, data: change.data, version: change.version })
                    localVersions.set(change.id, change.version)
                }
            }

            // === Order-Uebernahme-Entscheidung (Lesson 100) ===
            // Die Server-Order darf NICHT bedingungslos uebernommen werden:
            //
            //  - `_pendingReorder` wurde WAEHREND des Roundtrips (erneut)
            //    gesetzt → die lokale Order ist neuer als alles, was dieser
            //    Sync gepusht oder empfangen hat. Uebernahme wuerde frisch
            //    eingefuegte Absaetze aus der Store-Order werfen; der
            //    naechste Sync pushte dann eine stale Order, der Server
            //    machte die Absaetze zu Waisen (`missing` bleibt bei der
            //    Filter-only-Repair unangetastet) und der Cleanup unten
            //    loeschte sie lokal — Datenverlust ("Sync: N deleted from
            //    server", Incident 2026-07-14 Abend).
            //
            //  - Drift mit `missing`-IDs → der Server kennt Absaetze, die
            //    unsere gepushte Order nicht enthielt. Die reparierte
            //    Response-Order ist dann per Definition kein verlaessliches
            //    Loesch-Kriterium — nicht uebernehmen, nicht aufraeumen.
            //    Der Self-Heal unten pusht eine frische Order.
            //
            // Reine `extra`/`duplicates`-Drift uebernimmt weiterhin (die
            // reparierte Order IST dort die Heilung), ebenso der Pull-Pfad
            // ohne Drift (Multi-Device-Deletes propagieren nur darueber).
            const localOrderIsNewer = this._pendingReorder.has(projectId)
            const driftMissing = (result.paragraphOrderDrift
                && Array.isArray(result.paragraphOrderDrift.missing))
                ? result.paragraphOrderDrift.missing
                : []
            const adoptServerOrder = !!result.serverParagraphOrder
                && !localOrderIsNewer
                && driftMissing.length === 0

            // Paragraph-Reihenfolge und Projekt-Version aktualisieren
            // (VOR dem Editor-Callback, damit loadProject die richtige Order sieht)
            let localParagraphsDeleted = 0
            if (adoptServerOrder) {
                // Phase 4.8: Server liefert v4-Order direkt — kein Adapter mehr.
                project.paragraphOrder = result.serverParagraphOrder

                // Lokale Paragraphen aufräumen die auf dem Server nicht mehr existieren.
                // Der Server liefert nur neue/geaenderte Paragraphen, aber keine
                // Loeschungen — die erkennen wir an der server-paragraphOrder.
                // ABER: Paragraphen mit Pending-Aenderungen nie loeschen —
                // auch nicht die, die WAEHREND des Roundtrips enqueued wurden
                // (Live-Set), z.B. ein frisch getippter Absatz (B1).
                const targetIds = new Set(project.paragraphOrder)
                for (const localId of localVersions.keys()) {
                    if (!targetIds.has(localId)
                            && !pendingIds.has(localId)
                            && !livePendingIds.has(localId)) {
                        await this._store.deleteParagraph(localId)
                        localParagraphsDeleted++
                    }
                }
            }
            // Projekt FRISCH aus IndexedDB laden — zwischen Start und Ende
            // des Syncs koennten andere Operationen (z.B. Cloud-Toggle)
            // das Projekt geaendert haben. Nur sync-relevante Felder updaten.
            //
            // Bug-Fix (Spec §16.1): updatedAt NICHT bei jedem Sync auf
            // serverTime setzen — sonst zeigt das Dashboard "just now" fuer
            // alle Cloud-Projekte permanent, weil der 60s-Poll bzw. der
            // initialSync das updatedAt unkonditional gebumpt hat. Korrekt
            // ist: nur dann auf serverTime setzen, wenn tatsaechlich
            // gemutiert wurde — Push (hasLocalChanges/hasReorder), Pull mit
            // angewandten Server-Changes (appliedChanges) oder lokale
            // Loeschungen wegen serverParagraphOrder-Drift. Beim reinen
            // Pull-Poll bleibt das urspruengliche updatedAt erhalten.
            const hadMutation = hasLocalChanges
                || hasReorder
                || appliedChanges.length > 0
                || localParagraphsDeleted > 0
            const freshProject = await this._store.getProject(projectId)
            if (freshProject) {
                if (adoptServerOrder) {
                    // Phase 4.8: Server liefert v4-Order direkt. Uebernahme
                    // nur unter der Order-Entscheidung oben (Lesson 100) —
                    // sonst clobbert die Response-Order hier die waehrend
                    // des Roundtrips frisch geflushte Store-Order.
                    freshProject.paragraphOrder = result.serverParagraphOrder
                } else if (result.serverParagraphOrder && appliedChanges.length > 0) {
                    // Adopt-Gate zu, aber der Pull hat Absaetze geliefert,
                    // die die lokale Order nicht kennt (server-seitig
                    // eingefuegt, z.B. Memory-Extraktor): anker-relativ
                    // einsortieren statt sie der loadProject-Toleranz zu
                    // ueberlassen, die sie ans Dokumentende haengen und
                    // beim naechsten Save als falsche Position pushen
                    // wuerde (Lesson 102). Kein _pendingReorder dafuer —
                    // der Server hat diese Positionen ja bereits.
                    const merged = insertPulledIdsByServerAnchor(
                        freshProject.paragraphOrder || [],
                        appliedChanges.filter(c => !c.deleted).map(c => c.id),
                        result.serverParagraphOrder
                    )
                    if (merged) freshProject.paragraphOrder = merged
                }
                if (result.newProjectVersion !== undefined) {
                    freshProject.version = result.newProjectVersion
                }
                // Live-Set ist die Wahrheit: enthaelt Sende-Reste UND
                // waehrend des Roundtrips frisch enqueute IDs (B1).
                freshProject.syncStatus = livePendingIds.size > 0 ? 'pending' : 'synced'
                if (hadMutation) {
                    freshProject.updatedAt = result.serverTime || new Date().toISOString()
                }
                if (result.serverTime) {
                    freshProject.lastSyncTime = result.serverTime
                }
                // sizeBytes vom Server uebernehmen — Server ist beim Pull
                // autoritativ (Push hat unseren Wert bereits einbezogen, ist
                // dann ein no-op). Erlaubt 0 (geclearte Projekte auf anderem
                // Geraet). Aelterer Server liefert das Feld nicht → wir
                // tasten den lokalen Wert nicht an.
                if (typeof result.serverSizeBytes === 'number'
                        && freshProject.sizeBytes !== result.serverSizeBytes) {
                    freshProject.sizeBytes = result.serverSizeBytes
                }
                await this._store.putProject(freshProject)
            }

            // Self-Heal fuer `missing`-Drift (Gegenstueck zum extra-Heal in
            // der Batch-Schleife oben): missing-IDs, die in der LOKALEN
            // Store-Order stehen, sind das Symptom einer stale gepushten
            // Order — ein frischer Order-Push heilt die Server-Order und
            // beendet den Drift. missing-IDs, die auch lokal nicht in der
            // Order stehen, sind echte Server-Waisen: bewusst NICHT healen
            // (sonst Sync-Loop alle 5s, weil der Push nichts aendert) —
            // Aufraeumen ist Sache eines dedizierten Cleanups, nicht des
            // Sync-Hotpaths.
            if (driftMissing.length > 0 && freshProject) {
                const localOrderIds = new Set(freshProject.paragraphOrder || [])
                if (driftMissing.some(id => localOrderIds.has(id))) {
                    this._pendingReorder.add(projectId)
                    this._scheduleSyncForProject(projectId)
                }
            }

            // Aktiven Editor benachrichtigen wenn sich tatsaechlich etwas geaendert hat
            if ((appliedChanges.length > 0 || localParagraphsDeleted > 0) && this.onRemoteChanges) {
                this.onRemoteChanges(projectId, appliedChanges, localParagraphsDeleted)
            }

            // Sync-Zeitpunkt aktualisieren und sofort persistieren
            if (result.serverTime) {
                this._lastSyncTimes.set(projectId, result.serverTime)
                this._persistSyncTimes()
            }

            // Pending bereinigen — nur leere Live-Sets aus den Maps nehmen.
            // (B1: kein pauschales Delete mehr — die Live-Sets koennen
            // frische, waehrend des Syncs enqueute IDs enthalten; die Flags
            // sind zu Sync-Beginn konsumiert worden.)
            if (livePendingIds.size === 0) {
                this._pendingSyncIds.delete(projectId)
            }
            if (livePendingDeletes.size === 0) {
                this._pendingDeleteIds.delete(projectId)
            }

        } catch (err) {
            // B1: konsumierte Flags zuruecklegen — sie wurden moeglicherweise
            // nicht gesendet; ein doppelter Send ist idempotent.
            if (hasReorder) this._pendingReorder.add(projectId)
            if (hasSpellcheckMeta) this._pendingSpellcheckMeta.add(projectId)
            console.warn('Sync fehlgeschlagen:', err.message)
            this._noteSyncTrouble()
        } finally {
            this._enqueuedDuringSync.delete(projectId)
            endTotal()
        }
    }

    /**
     * Erzwingt einen sofortigen Sync fuer ein Projekt. Bricht den laufenden
     * Debounce-Timer ab, wartet auf einen etwaig laufenden Sync und fuehrt
     * danach einen frischen syncProject() aus. Nach Rueckkehr sind alle
     * lokalen Aenderungen garantiert am Server.
     *
     * Geplanter Einsatz (Spec §2): Vor jedem Agent-Request, damit der
     * Server-seitige DB-Direktzugriff den aktuellen Dokument-Stand sieht.
     *
     * @param {string} projectId
     * @returns {Promise<void>}
     */
    async flushNow(projectId) {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return

        const timeout = this._syncTimeouts.get(projectId)
        if (timeout) {
            clearTimeout(timeout)
            this._syncTimeouts.delete(projectId)
        }

        // Auf etwaig laufenden Sync warten (promise-basiert, kein Polling).
        const inflight = this._syncing.get(projectId)
        if (inflight) {
            try { await inflight } catch { /* Fehler des Vor-Syncs sind hier egal */ }
        }

        // Frischen Sync starten (dedupliziert sich selbst ueber _syncing).
        await this.syncProject(projectId)
    }

    /**
     * Gibt die Anzahl der ausstehenden Sync-Aenderungen zurueck.
     * Fuer die UI (Tab-Leiste: orange Close-Button).
     *
     * @param {string} projectId
     * @returns {number}
     */
    getPendingCount(projectId) {
        const pending = this._pendingSyncIds.get(projectId)
        return pending ? pending.size : 0
    }

    /**
     * Prueft ob irgendein Projekt ausstehende Syncs hat.
     * @returns {boolean}
     */
    hasPendingChanges() {
        for (const [, pending] of this._pendingSyncIds) {
            if (pending.size > 0) return true
        }
        for (const [, pending] of this._pendingDeleteIds) {
            if (pending.size > 0) return true
        }
        return false
    }

    /**
     * Stellt Pending-State aus localStorage wieder her (nach Page-Reload).
     * Startet Sync-Timer fuer Projekte mit ausstehenden Aenderungen.
     */
    restorePendingState() {
        try {
            const raw = localStorage.getItem('carbisdale_sync_pending')
            if (!raw) return
            const data = JSON.parse(raw)
            // Format: { syncIds: {projId: [ids]}, deleteIds: {projId: [ids]},
            //           syncTimes: {projId: time}, folderChanges: [{...}],
            //           projectFolderMoves: [{projectId, folderId}],
            //           reorder: [projId] }
            if (data.syncIds) {
                for (const [projId, ids] of Object.entries(data.syncIds)) {
                    if (ids.length > 0) {
                        this._pendingSyncIds.set(projId, new Set(ids))
                        this._scheduleSyncForProject(projId)
                    }
                }
            }
            if (data.deleteIds) {
                for (const [projId, ids] of Object.entries(data.deleteIds)) {
                    if (ids.length > 0) {
                        this._pendingDeleteIds.set(projId, new Set(ids))
                        this._scheduleSyncForProject(projId)
                    }
                }
            }
            // Reorder-Flags wiederherstellen (V4, siehe savePendingState).
            if (Array.isArray(data.reorder)) {
                for (const projId of data.reorder) {
                    this._pendingReorder.add(projId)
                    this._scheduleSyncForProject(projId)
                }
            }
            if (data.syncTimes) {
                for (const [projId, time] of Object.entries(data.syncTimes)) {
                    this._lastSyncTimes.set(projId, time)
                }
            }
            // Folder-Pendings wiederherstellen (Dashboard Phase 4).
            let hasFolderPending = false
            if (Array.isArray(data.folderChanges)) {
                for (const c of data.folderChanges) {
                    const id = c.type === 'upsert' ? c.folder?.id : c.folderId
                    if (id) {
                        this._pendingFolderChanges.set(id, c)
                        hasFolderPending = true
                    }
                }
            }
            if (Array.isArray(data.projectFolderMoves)) {
                for (const move of data.projectFolderMoves) {
                    if (move.projectId) {
                        this._pendingProjectFolderMoves.set(move.projectId, { folderId: move.folderId || null })
                        hasFolderPending = true
                    }
                }
            }
            if (hasFolderPending) this._scheduleFolderSync()
            localStorage.removeItem('carbisdale_sync_pending')
        } catch {
            // Fehlerhafte Daten ignorieren
        }
    }

    /**
     * Speichert Pending-State in localStorage (vor Page-Unload).
     */
    savePendingState() {
        const syncIds = {}
        for (const [projId, ids] of this._pendingSyncIds) {
            if (ids.size > 0) syncIds[projId] = [...ids]
        }
        const deleteIds = {}
        for (const [projId, ids] of this._pendingDeleteIds) {
            if (ids.size > 0) deleteIds[projId] = [...ids]
        }
        const syncTimes = {}
        for (const [projId, time] of this._lastSyncTimes) {
            syncTimes[projId] = time
        }
        const folderChanges = [...this._pendingFolderChanges.values()]
        const projectFolderMoves = [...this._pendingProjectFolderMoves.entries()]
            .map(([projectId, val]) => ({ projectId, folderId: val.folderId }))
        // Reorder-Flags persistieren (V4): seit paragraphOrder nur noch bei
        // hasReorder mitgeht, darf ein Reload zwischen Strukturaenderung und
        // Sync das Flag nicht verlieren — sonst kaemen die Upserts an, die
        // Order nie, und der naechste Pull wuerde die neuen Absaetze lokal
        // loeschen (sie fehlen in der stale Server-Order).
        const reorder = [...this._pendingReorder]
        // Immer speichern — syncTimes muessen auch ohne Pending-Changes
        // erhalten bleiben, sonst sendet der naechste Sync lastSyncTime='0'
        // und der Server liefert alle Paragraphen zurueck
        localStorage.setItem('carbisdale_sync_pending', JSON.stringify({
            syncIds, deleteIds, syncTimes, folderChanges, projectFolderMoves, reorder
        }))
    }

    /**
     * Raeumt Timer auf (bei App-Beendigung).
     */
    destroy() {
        this.savePendingState()
        this.stopPolling()
        for (const [, timeout] of this._syncTimeouts) {
            clearTimeout(timeout)
        }
        this._syncTimeouts.clear()
        if (this._folderSyncTimeout) {
            clearTimeout(this._folderSyncTimeout)
            this._folderSyncTimeout = null
        }
    }

    // ─────────────────────────────────────────────
    // Private Methoden
    // ─────────────────────────────────────────────

    /**
     * Speichert nur die Sync-Zeitpunkte in localStorage (nach jedem Sync).
     */
    _persistSyncTimes() {
        try {
            const raw = localStorage.getItem('carbisdale_sync_pending')
            const data = raw ? JSON.parse(raw) : {}
            const syncTimes = {}
            for (const [projId, time] of this._lastSyncTimes) {
                syncTimes[projId] = time
            }
            data.syncTimes = syncTimes
            localStorage.setItem('carbisdale_sync_pending', JSON.stringify(data))
        } catch {
            // localStorage-Fehler ignorieren
        }
    }

    /**
     * Poll: Sync fuer das aktive Projekt ausfuehren (Pull-Only wenn keine lokalen Aenderungen).
     * Wird periodisch und bei Visibility-Change aufgerufen.
     */
    async _pollActiveProject() {
        if (!this._activeProjectId) return
        if (!this._authService.isLoggedIn) return

        // B4: haengendes Offline-Flag aktiv heilen. Wenn der Server beim
        // Boot nicht erreichbar war, das Netz aber an ist, feuert der
        // Browser nie ein 'online'-Event — der 60s-Poll ist dann die
        // einzige Stelle, die die Rueckkehr des Servers bemerken kann.
        // checkAuth() setzt isOffline bei Erfolg selbst zurueck.
        if (this._authService.isOffline) {
            try { await this._authService.checkAuth() }
            catch { /* weiterhin offline — naechster Poll versucht es erneut */ }
            if (this._authService.isOffline || !this._authService.isLoggedIn) return
        }

        try {
            await this.syncProject(this._activeProjectId)
        } catch (err) {
            // Poll-Fehler nicht eskalieren — naechster Poll versucht es erneut
            console.warn('Poll-Sync fehlgeschlagen:', err.message)
        }
    }

    /**
     * Startet den 5s-Debounce-Timer fuer den Folder-Sync (user-scoped).
     */
    _scheduleFolderSync() {
        if (this._folderSyncTimeout) clearTimeout(this._folderSyncTimeout)
        this._folderSyncTimeout = setTimeout(() => {
            this._folderSyncTimeout = null
            this._runFolderSync()
        }, FOLDER_SYNC_DELAY)
    }

    /**
     * Eigentlicher Folder-Sync — POST /api/folders/sync mit Folder-Aenderungen
     * + Project-Folder-Moves. Push+Pull (Server liefert vollstaendige Folder-
     * Liste zurueck, damit Loeschungen auf anderen Geraeten reinkommen).
     * Promise-dedup analog zum Projekt-Sync.
     * @returns {Promise<void>}
     */
    _runFolderSync() {
        if (this._folderSyncing) return this._folderSyncing

        const promise = this._doFolderSync().finally(() => {
            if (this._folderSyncing === promise) {
                this._folderSyncing = null
            }
        })
        this._folderSyncing = promise
        return promise
    }

    /**
     * Spiegelt eine Server-Folder-Liste 1:1 in den lokalen Store:
     *   - vorhandene Folder via `applyServerFolder` (Last-Write-Wins ueber Version)
     *   - lokale Folder ohne Server-Match UND ohne pending-Push via
     *     `applyServerFolderDelete` (Server ist Source of Truth — sonst bleiben
     *     "Folder-Zombies" stehen, die ein anderes Geraet inzwischen geloescht hat)
     *
     * Geteilte Logik fuer `initialSync` (Boot/Login) und `_doFolderSync`
     * (laufender Folder-Sync); ohne den Helper drifteten die zwei Pfade
     * auseinander — `initialSync` hatte historisch nur Apply ohne Cleanup.
     *
     * @param {Array<Object>|null|undefined} serverFolders
     */
    async _applyServerFolderList(serverFolders) {
        if (!this._folderManager) return
        if (!Array.isArray(serverFolders)) return

        const serverIds = new Set()
        for (const folder of serverFolders) {
            serverIds.add(folder.id)
            try {
                await this._folderManager.applyServerFolder(folder)
            } catch (err) {
                console.warn(`[SyncService] applyServerFolder fehlgeschlagen fuer ${folder.id}:`, err.message)
            }
        }
        const localFolders = await this._folderManager.getAllFolders()
        for (const local of localFolders) {
            if (!serverIds.has(local.id) && !this._pendingFolderChanges.has(local.id)) {
                try {
                    await this._folderManager.applyServerFolderDelete(local.id)
                } catch (err) {
                    console.warn(`[SyncService] applyServerFolderDelete fehlgeschlagen fuer ${local.id}:`, err.message)
                }
            }
        }
    }

    async _doFolderSync() {
        if (!this._authService.isLoggedIn || this._authService.isOffline) return
        if (this._pendingFolderChanges.size === 0
                && this._pendingProjectFolderMoves.size === 0) return

        const snapshot = this.snapshotFolderPending()
        try {
            const res = await fetch('/api/folders/sync', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    folderChanges: snapshot.folderChanges.map(c => c.type === 'upsert'
                        ? { type: 'upsert', folder: c.folder }
                        : { type: 'delete', folderId: c.folderId }
                    ),
                    projectFolderMoves: snapshot.projectFolderMoves
                })
            })

            if (!res.ok) {
                if (res.status === 401) handleSessionLost()
                console.warn(`Folder-Sync fehlgeschlagen: ${res.status}`)
                return
            }

            const result = await res.json()

            // Push erfolgreich → die im Snapshot enthaltenen Pendings konsumieren.
            // Edits, die WAEHREND des Roundtrips reinkamen, bleiben in der Queue
            // und werden vom naechsten Folder-Sync gepusht.
            for (const c of snapshot.folderChanges) {
                const id = c.type === 'upsert' ? c.folder.id : c.folderId
                if (this._pendingFolderChanges.get(id) === c) {
                    this._pendingFolderChanges.delete(id)
                }
            }
            // failedMoves vom Server: projects, deren Ziel-Folder nicht
            // (mehr) existiert. Diese Pending-Moves NICHT konsumieren —
            // der naechste Folder-Sync (nach dem Pull des fehlenden
            // Folders) versucht es erneut. Ohne den Schutz verschwaende
            // der Client den intendierten Move stillschweigend.
            const failedMoves = new Set(Array.isArray(result.failedMoves) ? result.failedMoves : [])
            for (const move of snapshot.projectFolderMoves) {
                if (failedMoves.has(move.projectId)) continue
                const local = this._pendingProjectFolderMoves.get(move.projectId)
                if (local && (local.folderId || null) === (move.folderId || null)) {
                    this._pendingProjectFolderMoves.delete(move.projectId)
                }
            }

            // Pull: Server-Folder-Liste 1:1 in den lokalen Store schreiben.
            await this._applyServerFolderList(result.folders)
        } catch (err) {
            console.warn('Folder-Sync fehlgeschlagen:', err.message)
        }
    }

    /**
     * Startet den 5s-Debounce-Timer fuer ein Projekt.
     */
    _scheduleSyncForProject(projectId) {
        // Bestehenden Timer abbrechen
        const existing = this._syncTimeouts.get(projectId)
        if (existing) clearTimeout(existing)

        const timeout = setTimeout(() => {
            this._syncTimeouts.delete(projectId)
            this.syncProject(projectId)
        }, SYNC_DELAY)

        this._syncTimeouts.set(projectId, timeout)
    }

    /**
     * Laedt alle Paragraphen eines Projekts vom Server (Initial-Sync).
     *
     * Phase 4.8: Server liefert v4-Records direkt (nach Migration 008) —
     * Records werden 1:1 in den lokalen Store geschrieben.
     */
    async _pullAllParagraphs(projectId) {
        try {
            const res = await fetch('/api/sync', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    projectId,
                    projectVersion: 0,
                    lastSyncTime: '0',
                    changes: []
                })
            })

            if (!res.ok) {
                if (res.status === 401) handleSessionLost()
                return
            }

            const result = await res.json()

            // Phase 4.8: Server liefert v4-Records direkt — kein Adapter mehr.
            for (const change of (result.serverChanges || [])) {
                if (change.type === 'delete') {
                    await this._store.deleteParagraph(change.id)
                    continue
                }
                await this._store.putParagraph({
                    id: change.id,
                    projectId,
                    data: change.data,
                    version: change.version,
                    updatedAt: change.updatedAt
                })
            }

            // Projekt mit serverParagraphOrder updaten.
            if (result.serverParagraphOrder) {
                const project = await this._store.getProject(projectId)
                if (project) {
                    project.paragraphOrder = result.serverParagraphOrder
                    await this._store.putProject(project)
                }
            }
        } catch (err) {
            console.warn(`Pull-All fehlgeschlagen fuer Projekt ${projectId}:`, err.message)
        }
    }
}
